function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FunctionalLoader } from 'seagull';
import compact from 'lodash/compact';
import { nameValidation } from 'validators';
import { ConfirmationModal, Grid, GridItem, MenuItem, TextField, Typography } from 'seagull';
import { useEventReporter, useGlobalContext, useStore, useStoreActions } from "../../contexts";
import { useResponsive } from "../../client-flow/contexts";
import { ResponsiveModal } from "../../client-flow/components/ResponsiveModal";
import { useOnMount } from "../../hooks/useOnMount";
import { handlePromise } from "../../infra/utils";
import { connectControllerToTextField } from "../../utils/react-hook-form.utils";
import { memberModel } from "../../models/member";
import { UserRole } from "../../store/store.model";
import { selectors } from "../../store/state-selectors";
import styles from "./InviteOrEditMemberModal.module.scss";
import { ErrorText } from "../../constants";
import { ErrorResponse, ServerError } from "../../infra/ErrorResponse";
import { validateContact } from "../../business-logic/validators";
const testPrefix = 'invite-member-modal';
const defaultRole = UserRole.Member;
export default function InviteOrEditMemberModal(props) {
  const {
    isResponsive,
    member,
    allMembers,
    onClose,
    onSuccess
  } = props;
  const globalContext = useGlobalContext();
  const worksAt = useStore(selectors.user.business);
  const userRole = useStore(selectors.user.role);
  const isVendor = useStore(selectors.user.isVendor);
  const userId = useStore(selectors.user.id);
  const {
    setWorksAt,
    api,
    logBI,
    fetchUserInfo
  } = useStoreActions();
  const {
    reportFormSubmission,
    prepareFormObject
  } = useEventReporter();
  const isEditMode = !!member;
  const {
    control,
    formState,
    handleSubmit
  } = useForm({
    mode: 'all',
    defaultValues: isEditMode ? {
      firstName: member.firstName,
      lastName: member.lastName,
      email: member.email,
      role: memberModel.effectiveRole(member)
    } : {
      firstName: '',
      lastName: '',
      email: '',
      role: ''
    }
  });
  const {
    isDesktop
  } = useResponsive();
  const [roleDropdownEl, setRoleDropdownEl] = useState(null);
  const [existingMembers, setExistingMembers] = useState(allMembers);
  const firstNameValidator = nameValidation('First name', {
    required: 'Please enter a first name'
  });
  const lastNameValidator = nameValidation('Last name', {
    required: 'Please enter a last name'
  });
  const roleValidator = nameValidation('Role', {
    required: 'Please enter a role'
  });
  useOnMount(() => {
    async function retrieveMembers() {
      try {
        const {
          members
        } = await api.member.getAll();
        setExistingMembers(members);
      } catch (err) {
        globalContext.showErrorToast(ErrorText.UnexpectedErrorTryAgain, 'failed-to-load-members-toast');
        onClose();
      }
    }
    if (!existingMembers) {
      // relevant in the client onboarding and payment method tab
      retrieveMembers();
    }
  });
  const onValidSubmit = async _ref => {
    let {
      firstName,
      lastName,
      email,
      role
    } = _ref;
    if (isEditMode) {
      const isEditingCurrentUser = member.id === userId;
      const payload = {
        id: member.id,
        currentRole: memberModel.effectiveRole(member),
        newRole: role || defaultRole
      };
      logBI(12200, _objectSpread(_objectSpread({}, payload), {}, {
        email: member.email
      }));
      const changeRoleLoader = 'changeRole';
      globalContext.startLoader(changeRoleLoader);
      const result = await handlePromise(api.member.changeRole(payload));
      if (!result.success) {
        globalContext.stopLoader(changeRoleLoader);
        return globalContext.showErrorToast('Failed to change role', 'cr-fail');
      }
      if (isEditingCurrentUser) {
        await handlePromise(fetchUserInfo());
      }
      const userChangedHimselfToMember = isEditingCurrentUser && role === UserRole.Member;
      if (!userChangedHimselfToMember) {
        // When a user changes his own role to member he's no longer allowed to access member APIs (which is what onSuccess does).
        onSuccess && (await onSuccess());
      }
      globalContext.stopLoader(changeRoleLoader);
      onClose();
    } else {
      try {
        const invitedRole = role || defaultRole;
        logBI(12201, {
          email,
          role: invitedRole
        });
        await api.member.invite({
          firstName,
          lastName,
          email,
          role: invitedRole
        });
        if (worksAt) {
          setWorksAt(_objectSpread(_objectSpread({}, worksAt), {}, {
            membersCount: worksAt.membersCount + 1
          }));
        }
        reportFormSubmission('team-invited-member', [prepareFormObject('email', email), prepareFormObject('firstName', firstName), prepareFormObject('lastName', lastName)]);
        onSuccess && (await onSuccess());
        globalContext.showSuccessToast("Invite sent to ".concat(email), 'sent-invite-succsessfully-toast');
      } catch (err) {
        const e = new ErrorResponse(err);
        const text = e.hasServerError(ServerError.PendingInvitationAlreadyExists) ? 'There is already a pending invitation for this email' : e.hasServerError([ServerError.UserAlreadyAffiliatedWithBusiness, ServerError.UserAlreadyInBusiness]) ? 'This email already belongs to a member of your team' : ErrorText.UnexpectedErrorTryAgain;
        globalContext.showErrorToast(text);
      } finally {
        onClose();
      }
    }
  };
  const emailRules = () => ({
    validate: {
      email: validateContact.email,
      emailAlreadyUsed: email => (existingMembers === null || existingMembers === void 0 ? void 0 : existingMembers.every(m => m.email !== email)) || 'This email already belongs to a member of your team'
    }
  });
  const roleOptions = compact([{
    value: UserRole.Owner,
    text: 'Owner',
    subtext: 'Has access to everything',
    disabled: userRole !== UserRole.Owner && userRole !== UserRole.AnchorAdmin
  }, isVendor && {
    value: UserRole.Admin,
    text: 'Admin',
    subtext: 'Has access to everything except for changing your bank account'
  }, {
    value: UserRole.Member,
    text: 'Member',
    subtext: isVendor ? 'Has access to everything needed to manage a client but does not have access to your business settings or payouts' : 'Can view all and add payment methods. Cannot approve proposals, remove payment methods, approve amendments/invoices, or change business settings.'
  }]);
  const commonModalProps = {
    ['data-testid']: testPrefix,
    title: "".concat(isEditMode ? 'Edit' : 'Invite', " a team member"),
    onClose,
    primaryButtonLabel: isEditMode ? 'Save' : 'Send Invitation',
    primaryButtonDisabled: formState.isSubmitting || isEditMode && !formState.isDirty,
    onPrimaryButtonClick: handleSubmit(onValidSubmit),
    secondaryButtonLabel: 'Cancel',
    onSecondaryButtonClick: onClose
  };
  const modalContent = /*#__PURE__*/React.createElement(Grid, {
    className: styles.modalContent
  }, /*#__PURE__*/React.createElement(GridItem, {
    span: isDesktop ? 6 : 12
  }, /*#__PURE__*/React.createElement(Controller, {
    control: control,
    name: "firstName",
    rules: firstNameValidator,
    render: renderProps => /*#__PURE__*/React.createElement(TextField, _extends({
      disabled: isEditMode,
      label: "First name",
      "data-testid": "".concat(testPrefix, "-input-first-name")
    }, connectControllerToTextField(renderProps), firstNameValidator.dataAttr))
  })), /*#__PURE__*/React.createElement(GridItem, {
    span: isDesktop ? 6 : 12
  }, /*#__PURE__*/React.createElement(Controller, {
    control: control,
    name: "lastName",
    rules: lastNameValidator,
    render: renderProps => /*#__PURE__*/React.createElement(TextField, _extends({
      disabled: isEditMode,
      label: "Last name",
      "data-testid": "".concat(testPrefix, "-input-last-name")
    }, connectControllerToTextField(renderProps), lastNameValidator.dataAttr))
  })), /*#__PURE__*/React.createElement(GridItem, {
    span: 12
  }, /*#__PURE__*/React.createElement(Controller, {
    control: control,
    name: "email",
    rules: isEditMode ? undefined : emailRules(),
    render: renderProps => /*#__PURE__*/React.createElement(TextField, _extends({
      disabled: isEditMode,
      label: "Email",
      "data-testid": "".concat(testPrefix, "-input-email")
    }, connectControllerToTextField(renderProps)))
  })), /*#__PURE__*/React.createElement(GridItem, {
    span: 12
  }, /*#__PURE__*/React.createElement(Controller, {
    control: control,
    name: "role",
    rules: roleValidator,
    render: p => /*#__PURE__*/React.createElement(TextField, _extends({
      select: true,
      label: "Role",
      ref: setRoleDropdownEl,
      "data-testid": "".concat(testPrefix, "-input-role"),
      placeholder: "Select a role"
    }, connectControllerToTextField(p)), roleOptions.map(_ref2 => {
      let {
        value,
        text,
        subtext,
        disabled
      } = _ref2;
      return /*#__PURE__*/React.createElement(MenuItem, {
        key: value,
        variant: "multiline",
        value: value,
        disabled: disabled,
        label: text,
        "data-testid": "".concat(testPrefix, "-role-opt-").concat(value),
        style: {
          width: roleDropdownEl === null || roleDropdownEl === void 0 ? void 0 : roleDropdownEl.clientWidth
        }
      }, /*#__PURE__*/React.createElement(Typography, {
        weight: "normal"
      }, text), /*#__PURE__*/React.createElement(Typography, null, subtext));
    }))
  })));
  if (!existingMembers) {
    return /*#__PURE__*/React.createElement(FunctionalLoader, {
      show: true,
      name: "fetch-members-loader"
    });
  }
  return isResponsive ? /*#__PURE__*/React.createElement(ResponsiveModal, _extends({
    isDesktop: isDesktop,
    backTitle: "Back to agreement"
  }, commonModalProps), modalContent) : /*#__PURE__*/React.createElement(ConfirmationModal, _extends({}, commonModalProps, {
    textAlignCenter: false,
    content: modalContent
  }));
}